import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash, PencilSimple, CheckCircle, BellRinging, MagnifyingGlass, DownloadSimple, ListBullets, Calendar } from "@phosphor-icons/react";
import GlossaryText from "../components/GlossaryText";
import TrackerCalendar from "../components/TrackerCalendar";

const isCipStd = (sid) => String(sid || "").toUpperCase().startsWith("CIP-");

const FREQ_OPTIONS = [
  ["quarterly", "Quarterly (must fall in the following quarter)"],
  ["annual", "Annual / calendar year"],
  ["calendar_months", "Every N calendar months…"],
  ["calendar_years", "Every N calendar years…"],
  ["days:30", "Every 30 calendar days"],
  ["days:35", "Every 35 calendar days"],
  ["days:60", "Every 60 calendar days"],
  ["days:90", "Every 90 calendar days"],
];

const freqToToken = (f) => {
  if (!f) return "quarterly";
  if (f.kind === "quarterly") return "quarterly";
  if (f.kind === "annual") return "annual";
  if (f.kind === "calendar_months") return "calendar_months";
  if (f.kind === "calendar_years") return "calendar_years";
  if (f.kind === "days") return `days:${f.days || 30}`;
  return "quarterly";
};
const tokenToFreq = (token, months, years) => {
  if (token === "quarterly") return { kind: "quarterly" };
  if (token === "annual") return { kind: "annual" };
  if (token === "calendar_months") return { kind: "calendar_months", months: Number(months) || 15 };
  if (token === "calendar_years") return { kind: "calendar_years", years: Number(years) || 12 };
  if (token.startsWith("days:")) return { kind: "days", days: Number(token.split(":")[1]) };
  return { kind: "quarterly" };
};

const STATUS_BADGE = {
  overdue: "bg-red-100 text-red-700 border-red-300",
  due_soon: "bg-amber-100 text-amber-700 border-amber-300",
  upcoming: "bg-emerald-50 text-emerald-700 border-emerald-300",
};
const STATUS_LABEL = { overdue: "Overdue", due_soon: "Due soon", upcoming: "Upcoming" };

const blankForm = (track) => ({
  id: null, track, standard_id: "", req_number: "", subpart: "", description: "", sme: "",
  freqToken: "quarterly", months: 15, years: 12, last_completed: "", next_due: "",
});

export default function Tracker({ track }) {
  const { canEdit, isRole } = useAuth();
  const isCip = track === "cip";
  const title = isCip ? "CIPWatch" : "OPWatch";
  const subtitle = isCip
    ? "Recurring CIP compliance tasks — track repeating obligations and due dates"
    : "Recurring Operations & Planning tasks — track repeating obligations and due dates";

  const [items, setItems] = useState([]);
  const [standards, setStandards] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [sortBy, setSortBy] = useState("standard"); // "standard" | "due"
  const [statusFilter, setStatusFilter] = useState("all"); // all | currently_enforceable | future_enforceable
  const [selected, setSelected] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankForm(track));
  const [completing, setCompleting] = useState(null);
  const [completeDate, setCompleteDate] = useState("");
  const [completeNote, setCompleteNote] = useState("");
  const [reminders, setReminders] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // list | calendar
  // Scan / auto-detect recurring requirements
  const [scanOpen, setScanOpen] = useState(false);
  const [scanCands, setScanCands] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanSme, setScanSme] = useState("");
  const [includeFuture, setIncludeFuture] = useState(false);

  const load = useCallback(() => {
    api.get(`/tracker?track=${track}`).then((r) => setItems(r.data));
  }, [track]);

  useEffect(() => {
    load();
    api.get("/standards").then((r) => setStandards(r.data || []));
    api.get("/requirements").then((r) => setReqs(r.data || []));
    if (isRole("admin")) api.get("/admin/tracker-settings").then((r) => setReminders(r.data)).catch(() => {});
  }, [load, isRole]);

  const trackStandards = useMemo(
    () => standards.filter((s) => (isCip ? isCipStd(s.standard_id) : !isCipStd(s.standard_id))),
    [standards, isCip]
  );
  // Lookup Standard title by its ID for the hover-tooltip on the List view.
  const stdTitleById = useMemo(
    () => Object.fromEntries((standards || []).map((s) => [s.standard_id, s.title || ""])),
    [standards]
  );
  const formReqs = useMemo(
    () => reqs.filter((r) => r.standard_id === form.standard_id),
    [reqs, form.standard_id]
  );
  const formSubparts = useMemo(() => {
    const r = formReqs.find((x) => x.req_number === form.req_number);
    return (r?.sub_requirements || []).map((sp) => sp.label).filter(Boolean);
  }, [formReqs, form.req_number]);
  // Full requirement + subpart language for a tracked item (for hover-to-define display).
  const langFor = useCallback((it) => {
    const r = (reqs || []).find((x) => x.standard_id === it.standard_id && x.req_number === it.req_number);
    if (!r) return { text: it.description || "", part: "" };
    const part = it.subpart ? ((r.sub_requirements || []).find((s) => s.label === it.subpart)?.text || "") : "";
    return { text: r.text || "", part };
  }, [reqs]);
  const smeOptions = useMemo(
    () => Array.from(new Set(items.map((i) => i.sme).filter(Boolean))),
    [items]
  );
  // Default order: Standard -> Requirement -> Subpart (natural). Toggle to due date.
  const sortedItems = useMemo(() => {
    const arr = items.filter((it) => statusFilter === "all" || (it.enforcement_status || "currently_enforceable") === statusFilter);
    if (sortBy === "due") {
      arr.sort((a, b) => (a.next_due || "9999").localeCompare(b.next_due || "9999"));
    } else {
      arr.sort((a, b) =>
        (a.standard_id || "").localeCompare(b.standard_id || "", undefined, { numeric: true }) ||
        (a.req_number || "").localeCompare(b.req_number || "", undefined, { numeric: true }) ||
        (a.subpart || "").localeCompare(b.subpart || "", undefined, { numeric: true }));
    }
    return arr;
  }, [items, sortBy, statusFilter]);
  const futureItemCount = useMemo(() => items.filter((it) => it.enforcement_status === "future_enforceable").length, [items]);
  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const allSelected = sortedItems.length > 0 && sortedItems.every((it) => selected[it.id]);
  const toggleSel = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleSelAll = (v) => setSelected(v ? Object.fromEntries(sortedItems.map((it) => [it.id, true])) : {});
  const removeSelected = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Remove ${selectedIds.length} tracked item${selectedIds.length === 1 ? "" : "s"}? This stops tracking them.`)) return;
    try {
      const { data } = await api.post("/tracker/bulk-delete", { ids: selectedIds });
      setSelected({}); load();
      toast.success(`Removed ${data.deleted} item${data.deleted === 1 ? "" : "s"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  // Ensure the current frequency token is always selectable, even non-standard
  // detected day intervals (e.g. "Every 180 days" from a scan).
  const freqOptions = useMemo(() => {
    const opts = [...FREQ_OPTIONS];
    if (form.freqToken && form.freqToken.startsWith("days:") && !opts.some(([v]) => v === form.freqToken)) {
      opts.push([form.freqToken, `Every ${form.freqToken.split(":")[1]} days`]);
    }
    return opts;
  }, [form.freqToken]);

  const openCreate = () => { setForm(blankForm(track)); setOpen(true); };
  const openEdit = (it) => {
    setForm({
      id: it.id, track: it.track, standard_id: it.standard_id || "", req_number: it.req_number || "",
      subpart: it.subpart || "", description: it.description || "", sme: it.sme || "",
      freqToken: freqToToken(it.frequency), months: it.frequency?.months || 15, years: it.frequency?.years || 12,
      last_completed: it.last_completed || "", next_due: it.next_due || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.standard_id) return toast.error("Select a standard");
    if (!form.description.trim()) return toast.error("Add a brief description");
    const payload = {
      track, standard_id: form.standard_id, req_number: form.req_number, subpart: form.subpart,
      description: form.description, sme: form.sme,
      frequency: tokenToFreq(form.freqToken, form.months, form.years),
      last_completed: form.last_completed || null,
      next_due: form.next_due || null,
    };
    try {
      if (form.id) await api.put(`/tracker/${form.id}`, payload);
      else await api.post("/tracker", payload);
      setOpen(false); load();
      toast.success(form.id ? "Tracked item updated" : "Tracked item added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this tracked item?")) return;
    await api.delete(`/tracker/${id}`); load(); toast.success("Deleted");
  };

  const openComplete = (it) => { setCompleting(it); setCompleteDate(new Date().toISOString().slice(0, 10)); setCompleteNote(""); };
  const confirmComplete = async () => {
    try {
      await api.post(`/tracker/${completing.id}/complete`, { completed_at: completeDate, note: completeNote });
      setCompleting(null); load(); toast.success("Marked complete — next due date rolled forward");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleReminders = async (v) => {
    const next = { ...reminders, reminders_enabled: v };
    setReminders(next);
    try { await api.put("/admin/tracker-settings", next); toast.success(`Reminders ${v ? "enabled" : "disabled"}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.get(`/tracker/scan?track=${track}`);
      if (!data.length) { toast.info("No new recurring requirements found in your applicable standards."); return; }
      const currentCount = data.filter((c) => c.status !== "future_enforceable").length;
      setScanCands(data.map((c) => ({ ...c, selected: c.status !== "future_enforceable" })));
      // If everything found is future-enforceable, reveal those by default.
      setIncludeFuture(currentCount === 0);
      setScanSme("");
      setScanOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setScanning(false); }
  };
  const visibleCands = includeFuture ? scanCands : scanCands.filter((c) => c.status !== "future_enforceable");
  const futureCount = scanCands.filter((c) => c.status === "future_enforceable").length;
  const scanSelectedCount = scanCands.filter((c) => c.selected).length;
  const allVisibleSelected = visibleCands.length > 0 && visibleCands.every((c) => c.selected);
  const toggleScanAll = (v) => setScanCands((cs) => cs.map((c) => (visibleCands.includes(c) ? { ...c, selected: v } : c)));
  const toggleScanOne = (cand) => setScanCands((cs) => cs.map((c) => c === cand ? { ...c, selected: !c.selected } : c));
  const toggleIncludeFuture = (v) => {
    setIncludeFuture(v);
    if (!v) setScanCands((cs) => cs.map((c) => c.status === "future_enforceable" ? { ...c, selected: false } : c));
  };

  const exportFile = async (format) => {
    try {
      const res = await api.get(`/tracker/export?track=${track}&format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.toLowerCase()}_${new Date().toISOString().slice(0, 10)}.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed. Please try again."); }
  };
  const exportBrief = async () => {
    try {
      const year = new Date().getFullYear();
      const res = await api.get(`/tracker/export-brief?track=${track}&year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.toLowerCase()}_brief_${year}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Brief downloaded — edit SME or Last Done and re-upload to sync.");
    } catch (e) { toast.error("Brief export failed. Please try again."); }
  };
  const [importFile, setImportFile] = useState(null);
  const [importDiff, setImportDiff] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const previewImport = async (f) => {
    if (!f) return;
    setImportBusy(true);
    setImportFile(f);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/tracker/import-working/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportDiff(data);
      setImportOpen(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not read that workbook.");
    } finally { setImportBusy(false); }
  };
  const applyImport = async () => {
    if (!importDiff?.changes?.length) return;
    setImportBusy(true);
    try {
      const { data } = await api.post("/tracker/import-working/apply", {
        changes: importDiff.changes,
      });
      toast.success(`Applied ${data.applied} change${data.applied === 1 ? "" : "s"} from the working calendar.`);
      setImportOpen(false); setImportDiff(null); setImportFile(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setImportBusy(false); }
  };
  const addScanned = async () => {
    const items = scanCands.filter((c) => c.selected).map((c) => ({
      track: c.track, standard_id: c.standard_id, req_number: c.req_number, subpart: c.subpart,
      description: c.description, sme: scanSme, frequency: c.frequency,
    }));
    if (!items.length) return toast.error("Select at least one item");
    try {
      const { data } = await api.post("/tracker/bulk", { items });
      setScanOpen(false); load();
      toast.success(`Added ${data.created} recurring item${data.created === 1 ? "" : "s"} to ${title}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}
        action={
          <div className="flex items-center gap-2">
            <Button data-testid="tracker-export-xlsx" onClick={() => exportFile("xlsx")} disabled={!items.length} variant="outline" className="rounded-none">
              <DownloadSimple size={16} className="mr-1.5" />Excel
            </Button>
            <Button data-testid="tracker-export-brief" onClick={exportBrief} disabled={!items.length} variant="outline" className="rounded-none" title="Editable 12-month brief — change SME or Last Done in Excel, then re-upload via Import edited to sync back. Colors: dark = completed month; bright green = due this month; amber = due soon; red = overdue.">
              <DownloadSimple size={16} className="mr-1.5" />Brief
            </Button>
            {canEdit && (
              <label data-testid="tracker-import-working-label" className="inline-flex items-center px-3 py-1.5 text-xs font-semibold border border-border rounded-none cursor-pointer hover:bg-secondary" title="Upload an edited Brief to re-sync SME and Last Done values">
                <DownloadSimple size={14} className="mr-1.5 rotate-180" />
                {importBusy ? "Reading…" : "Import edited"}
                <input data-testid="tracker-import-working-input" type="file" accept=".xlsx"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; previewImport(f); }} />
              </label>
            )}
            <Button data-testid="tracker-export-pdf" onClick={() => exportFile("pdf")} disabled={!items.length} variant="outline" className="rounded-none">
              <DownloadSimple size={16} className="mr-1.5" />PDF
            </Button>
            {canEdit && (
              <>
                <Button data-testid="tracker-scan-btn" onClick={runScan} disabled={scanning} variant="outline" className="rounded-none">
                  <MagnifyingGlass size={16} className="mr-1.5" />{scanning ? "Scanning…" : "Scan for recurring"}
                </Button>
                <Button data-testid="tracker-add-btn" onClick={openCreate} className="rounded-none bg-primary hover:bg-primary/90">
                  <Plus size={16} className="mr-1.5" />Add tracked item
                </Button>
              </>
            )}
          </div>
        } />

      <div className="p-8 space-y-6">
        {reminders && (
          <div data-testid="tracker-reminders-toggle" className="flex items-center justify-between border border-border bg-card px-4 py-3">
            <div className="flex items-center gap-2 text-sm">
              <BellRinging size={18} className="text-muted-foreground" />
              <span>In-app due/overdue reminders for this entity</span>
            </div>
            <Switch checked={!!reminders.reminders_enabled} onCheckedChange={toggleReminders} data-testid="tracker-reminders-switch" />
          </div>
        )}

        {items.length === 0 ? (
          <Empty title={`No ${title} items yet`} hint={canEdit ? "Add a recurring task to start tracking its due dates." : "No recurring items have been added."} />
        ) : (
          <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="overline text-muted-foreground mr-1">View</span>
              <div className="inline-flex border border-border" data-testid="tracker-view-toggle">
                <button data-testid="tracker-view-list" onClick={() => setViewMode("list")}
                  className={`px-3 py-1 text-xs font-semibold inline-flex items-center gap-1.5 ${viewMode === "list" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
                  <ListBullets size={13} /> List
                </button>
                <button data-testid="tracker-view-calendar" onClick={() => setViewMode("calendar")}
                  className={`px-3 py-1 text-xs font-semibold border-l border-border inline-flex items-center gap-1.5 ${viewMode === "calendar" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
                  <Calendar size={13} /> Calendar
                </button>
              </div>
              {viewMode === "list" && (
                <>
                  <span className="overline text-muted-foreground ml-3 mr-1">Sort</span>
                  <div className="inline-flex border border-border">
                    <button data-testid="tracker-sort-standard" onClick={() => setSortBy("standard")}
                      className={`px-3 py-1 text-xs font-semibold ${sortBy === "standard" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
                      Standard &amp; subpart
                    </button>
                    <button data-testid="tracker-sort-due" onClick={() => setSortBy("due")}
                      className={`px-3 py-1 text-xs font-semibold border-l border-border ${sortBy === "due" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
                      Due date (earliest)
                    </button>
                  </div>
                  {futureItemCount > 0 && (
                    <div className="inline-flex border border-border ml-2" data-testid="tracker-status-filter">
                      {[["all", "All"], ["currently_enforceable", "Active"], ["future_enforceable", `Future (${futureItemCount})`]].map(([v, l], idx) => (
                        <button key={v} data-testid={`tracker-filter-${v}`} onClick={() => setStatusFilter(v)}
                          className={`px-3 py-1 text-xs font-semibold ${idx > 0 ? "border-l border-border" : ""} ${statusFilter === v ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
                          {l}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            {canEdit && viewMode === "list" && selectedIds.length > 0 && (
              <Button data-testid="tracker-remove-selected" onClick={removeSelected} variant="outline"
                className="rounded-none text-red-600 border-red-300 hover:bg-red-50">
                <Trash size={15} className="mr-1.5" />Remove selected ({selectedIds.length})
              </Button>
            )}
          </div>

          {viewMode === "calendar" ? (
            <TrackerCalendar track={track} title={title} />
          ) : (<div className="border border-border bg-card overflow-x-auto" data-testid="tracker-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/40 text-left">
                  {canEdit && (
                    <th className="px-3 py-2 w-8">
                      <input type="checkbox" data-testid="tracker-select-all" className="accent-primary"
                        checked={allSelected} onChange={(e) => toggleSelAll(e.target.checked)} />
                    </th>
                  )}
                  <th className="px-3 py-2 font-semibold">Standard</th>
                  <th className="px-3 py-2 font-semibold">Req.</th>
                  <th className="px-3 py-2 font-semibold">Subpart</th>
                  <th className="px-3 py-2 font-semibold">Requirement language</th>
                  <th className="px-3 py-2 font-semibold">SME</th>
                  <th className="px-3 py-2 font-semibold">Frequency</th>
                  <th className="px-3 py-2 font-semibold">Last completed</th>
                  <th className="px-3 py-2 font-semibold">Target date</th>
                  <th className="px-3 py-2 font-semibold">Compliance date</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  {canEdit && <th className="px-3 py-2 font-semibold text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((it) => (
                  <tr key={it.id} data-testid={`tracker-row-${it.id}`} className={`border-b border-border/60 hover:bg-secondary/20 align-top ${selected[it.id] ? "bg-primary/5" : ""}`}>
                    {canEdit && (
                      <td className="px-3 py-2">
                        <input type="checkbox" data-testid={`tracker-select-${it.id}`} className="accent-primary"
                          checked={!!selected[it.id]} onChange={() => toggleSel(it.id)} />
                      </td>
                    )}
                    <td className="px-3 py-2 font-mono font-semibold whitespace-nowrap">
                      <button type="button"
                              title={`${stdTitleById[it.standard_id] || it.standard_id} — click to copy code`}
                              data-testid={`tracker-std-${it.id}`}
                              onClick={() => {
                                const code = [it.standard_id, it.req_number, it.subpart ? `.${it.subpart}` : ""].filter(Boolean).join(" ").replace(" .", ".");
                                navigator.clipboard?.writeText(code);
                                toast.success(`Copied ${code}`);
                              }}
                              className="cursor-pointer border-b border-dotted border-muted-foreground/40 hover:text-primary hover:border-primary/60 bg-transparent p-0 font-mono font-semibold">
                        {it.standard_id}
                      </button>
                      {it.enforcement_status === "future_enforceable" && (
                        <span data-testid={`tracker-future-${it.id}`} className="ml-1.5 border border-amber-300 bg-amber-50 text-amber-700 px-1.5 py-0.5 text-[10px] font-semibold align-middle">Future</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono whitespace-nowrap">
                      {it.req_number ? (() => {
                        const lg = langFor(it);
                        const code = [it.standard_id, it.req_number, it.subpart ? `.${it.subpart}` : ""].filter(Boolean).join(" ").replace(" .", ".");
                        return (
                          <button type="button"
                                  title={lg.text ? `${lg.text}\n\n(click to copy ${code})` : `Click to copy ${code}`}
                                  data-testid={`tracker-req-${it.id}`}
                                  onClick={() => { navigator.clipboard?.writeText(code); toast.success(`Copied ${code}`); }}
                                  className="cursor-pointer border-b border-dotted border-muted-foreground/40 hover:text-primary hover:border-primary/60 bg-transparent p-0 font-mono">
                            {it.req_number}
                          </button>
                        );
                      })() : "—"}
                    </td>
                    <td className="px-3 py-2 font-mono whitespace-nowrap">
                      {it.subpart ? (() => {
                        const lg = langFor(it);
                        const code = [it.standard_id, it.req_number, it.subpart ? `.${it.subpart}` : ""].filter(Boolean).join(" ").replace(" .", ".");
                        return (
                          <button type="button"
                                  title={lg.part ? `${lg.part}\n\n(click to copy ${code})` : `Click to copy ${code}`}
                                  data-testid={`tracker-subpart-${it.id}`}
                                  onClick={() => { navigator.clipboard?.writeText(code); toast.success(`Copied ${code}`); }}
                                  className="cursor-pointer border-b border-dotted border-muted-foreground/40 hover:text-primary hover:border-primary/60 bg-transparent p-0 font-mono">
                            {it.subpart}
                          </button>
                        );
                      })() : "—"}
                    </td>
                    <td className="px-3 py-2 max-w-lg" data-testid={`tracker-lang-${it.id}`}>
                      {(() => { const lg = langFor(it); return (
                        <div className="space-y-1">
                          {lg.text && <div className="text-sm whitespace-pre-line leading-relaxed"><GlossaryText text={lg.text} /></div>}
                          {lg.part && (
                            <div className="text-xs whitespace-pre-line leading-relaxed pl-2 border-l border-border text-muted-foreground">
                              <span className="font-mono font-semibold text-foreground/70 mr-1">{it.subpart}</span><GlossaryText text={lg.part} />
                            </div>
                          )}
                          {it.description && (() => {
                            const note = it.description.replace(/[…\s]+$/, "");
                            const isPrefix = note && (lg.text || "").replace(/\s+/g, " ").startsWith(note.slice(0, Math.min(note.length, 120)));
                            return note && !isPrefix ? (
                              <div className="text-xs text-muted-foreground italic">Note: {it.description}</div>
                            ) : null;
                          })()}
                        </div>
                      ); })()}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{it.sme || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">{it.frequency_label}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{it.last_completed || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap font-semibold text-indigo-700 dark:text-indigo-300" data-testid={`tracker-target-${it.id}`}>{it.target_date || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap" data-testid={`tracker-compliance-${it.id}`}>
                      <span className={it.compliance_overdue ? "font-bold text-red-600 dark:text-red-400" : "font-semibold"}>{it.compliance_date || it.next_due || "—"}</span>
                      {it.compliance_overdue && <span className="ml-1 text-[9px] uppercase font-bold border border-red-400 bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-300 px-1">past</span>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span data-testid={`tracker-status-${it.id}`} className={`inline-block border px-2 py-0.5 text-xs font-semibold ${STATUS_BADGE[it.status] || ""}`}>
                        {STATUS_LABEL[it.status] || it.status}
                      </span>
                    </td>
                    {canEdit && (
                      <td className="px-3 py-2 whitespace-nowrap text-right">
                        <div className="inline-flex items-center gap-1">
                          <button data-testid={`tracker-complete-${it.id}`} onClick={() => openComplete(it)} title="Mark complete"
                            className="p-1.5 text-emerald-700 hover:bg-emerald-50 border border-transparent hover:border-emerald-200"><CheckCircle size={16} /></button>
                          <button data-testid={`tracker-edit-${it.id}`} onClick={() => openEdit(it)} title="Edit"
                            className="p-1.5 text-muted-foreground hover:bg-secondary"><PencilSimple size={16} /></button>
                          <button data-testid={`tracker-delete-${it.id}`} onClick={() => remove(it.id)} title="Delete"
                            className="p-1.5 text-red-600 hover:bg-red-50"><Trash size={16} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
          </>
        )}
      </div>

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg" data-testid="tracker-dialog">
          <DialogHeader><DialogTitle>{form.id ? "Edit tracked item" : `Add ${title} item`}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Configure the recurring tracked item details.</DialogDescription>
          <div className="space-y-3">
            <div>
              <Label>Standard</Label>
              <Select value={form.standard_id} onValueChange={(v) => setForm({ ...form, standard_id: v, req_number: "", subpart: "" })}>
                <SelectTrigger data-testid="tracker-standard" className="rounded-none"><SelectValue placeholder="Select a standard" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {trackStandards.map((s) => <SelectItem key={s.standard_id} value={s.standard_id}>{s.standard_id} — {s.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Requirement</Label>
                <Select value={form.req_number || "none"} onValueChange={(v) => setForm({ ...form, req_number: v === "none" ? "" : v, subpart: "" })} disabled={!form.standard_id}>
                  <SelectTrigger data-testid="tracker-req" className="rounded-none"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">— None —</SelectItem>
                    {formReqs.map((r) => <SelectItem key={r.id} value={r.req_number}>{r.req_number}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subpart</Label>
                <Select value={form.subpart || "none"} onValueChange={(v) => setForm({ ...form, subpart: v === "none" ? "" : v })} disabled={!formSubparts.length}>
                  <SelectTrigger data-testid="tracker-subpart" className="rounded-none"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">— None —</SelectItem>
                    {formSubparts.map((sp) => <SelectItem key={sp} value={sp}>{sp}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Brief description</Label>
              <Textarea data-testid="tracker-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2} className="rounded-none" placeholder="e.g. Quarterly review of CIP-007 patch sources" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>SME</Label>
                <Input data-testid="tracker-sme" list="tracker-sme-list" value={form.sme} onChange={(e) => setForm({ ...form, sme: e.target.value })}
                  className="rounded-none" placeholder="Responsible SME" />
                <datalist id="tracker-sme-list">{smeOptions.map((s) => <option key={s} value={s} />)}</datalist>
              </div>
              <div>
                <Label>Frequency</Label>
                <Select value={form.freqToken} onValueChange={(v) => setForm({ ...form, freqToken: v })}>
                  <SelectTrigger data-testid="tracker-frequency" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {freqOptions.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.freqToken === "calendar_months" && (
              <div>
                <Label>Number of calendar months</Label>
                <Input data-testid="tracker-months" type="number" min={1} max={120} value={form.months}
                  onChange={(e) => setForm({ ...form, months: e.target.value })} className="rounded-none w-32" />
              </div>
            )}
            {form.freqToken === "calendar_years" && (
              <div>
                <Label>Number of calendar years</Label>
                <Input data-testid="tracker-years" type="number" min={1} max={20} value={form.years}
                  onChange={(e) => setForm({ ...form, years: e.target.value })} className="rounded-none w-32" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Last completed (optional)</Label>
                <Input data-testid="tracker-last-completed" type="date" value={form.last_completed}
                  onChange={(e) => setForm({ ...form, last_completed: e.target.value, next_due: "" })} className="rounded-none" />
              </div>
              {!form.last_completed && (
                <div>
                  <Label>Compliance date (auto if blank)</Label>
                  <Input data-testid="tracker-next-due" type="date" value={form.next_due}
                    onChange={(e) => setForm({ ...form, next_due: e.target.value })} className="rounded-none" />
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{form.last_completed
              ? "Compliance & target dates are auto-calculated from the last-completed date + frequency (NERC calendar-period rules)."
              : "Set a last-completed date to auto-calculate dates, or type a compliance date here for a one-off."} The <span className="font-semibold text-indigo-700 dark:text-indigo-300">target date</span> is derived automatically — 3 calendar months earlier for month/annual/year cadences, 10 calendar days earlier for day cadences, or the start of the last month of the quarter for quarterly. “Annual / calendar year” follows your entity’s setting (Administration → Entity Profile).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
            <Button data-testid="tracker-save" onClick={save} className="rounded-none bg-primary hover:bg-primary/90">{form.id ? "Save changes" : "Add item"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark complete dialog */}
      <Dialog open={!!completing} onOpenChange={(v) => !v && setCompleting(null)}>
        <DialogContent className="rounded-none max-w-md" data-testid="tracker-complete-dialog">
          <DialogHeader><DialogTitle>Mark complete</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{completing?.standard_id} {completing?.req_number} — {completing?.description}</p>
            <div>
              <Label>Completion date</Label>
              <Input data-testid="tracker-complete-date" type="date" value={completeDate} onChange={(e) => setCompleteDate(e.target.value)} className="rounded-none" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea data-testid="tracker-complete-note" value={completeNote} onChange={(e) => setCompleteNote(e.target.value)} rows={2} className="rounded-none" />
            </div>
            <p className="text-xs text-muted-foreground">The next due date will roll forward by the item’s frequency and this completion will be logged.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleting(null)} className="rounded-none">Cancel</Button>
            <Button data-testid="tracker-complete-confirm" onClick={confirmComplete} className="rounded-none bg-primary hover:bg-primary/90">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scan review dialog */}
      <Dialog open={scanOpen} onOpenChange={setScanOpen}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="tracker-scan-dialog">
          <DialogHeader><DialogTitle>Recurring requirements found ({scanCands.length})</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground -mt-2">
            We scanned your applicable {isCip ? "CIP" : "Operations & Planning"} requirements for time-based language.
            Review the detected items below, optionally assign an SME, then add them to {title}.
          </DialogDescription>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label>Assign SME to all (optional)</Label>
              <Input data-testid="scan-sme" list="tracker-sme-list" value={scanSme} onChange={(e) => setScanSme(e.target.value)}
                className="rounded-none" placeholder="Responsible SME" />
            </div>
            <label className="flex items-center gap-2 text-xs pb-2 cursor-pointer">
              <input type="checkbox" data-testid="scan-select-all" className="accent-primary"
                checked={allVisibleSelected}
                onChange={(e) => toggleScanAll(e.target.checked)} />
              Select all
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer border border-border bg-secondary/30 px-3 py-2"
            data-testid="scan-include-future-row">
            <input type="checkbox" data-testid="scan-include-future" className="accent-primary"
              checked={includeFuture} onChange={(e) => toggleIncludeFuture(e.target.checked)} />
            <span>Also include <span className="font-semibold">future-enforceable</span> standards{futureCount ? ` (${futureCount})` : ""}</span>
            <span className="text-xs text-muted-foreground ml-auto">Retired standards are always excluded.</span>
          </label>
          <div className="border border-border max-h-72 overflow-y-auto divide-y divide-border/60" data-testid="scan-candidate-list">
            {visibleCands.map((c, i) => (
              <label key={`${c.standard_id}-${c.req_number}-${c.subpart}-${i}`}
                data-testid={`scan-cand-${i}`}
                className="flex items-start gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-secondary/30">
                <input type="checkbox" className="mt-1 accent-primary" checked={c.selected} onChange={() => toggleScanOne(c)} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold">{c.standard_id}</span>
                    <span className="font-mono text-xs">{c.req_number}{c.subpart ? ` · ${c.subpart}` : ""}</span>
                    <span className="border border-emerald-300 bg-emerald-50 text-emerald-700 px-1.5 text-xs font-semibold">{c.frequency_label}</span>
                    {c.status === "future_enforceable" && (
                      <span className="border border-amber-300 bg-amber-50 text-amber-700 px-1.5 text-xs font-semibold">Future</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 whitespace-pre-line leading-relaxed">
                    <span className="font-semibold text-foreground/70">matched “{c.matched}”</span>
                    <span className="block mt-0.5"><GlossaryText text={c.description} /></span>
                  </div>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScanOpen(false)} className="rounded-none">Cancel</Button>
            <Button data-testid="scan-add-selected" onClick={addScanned} disabled={!scanSelectedCount}
              className="rounded-none bg-primary hover:bg-primary/90">Add {scanSelectedCount} selected</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Working Calendar import — diff preview then apply */}
      <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) { setImportDiff(null); setImportFile(null); } }}>
        <DialogContent className="rounded-none max-w-4xl" data-testid="tracker-import-dialog">
          <DialogHeader>
            <DialogTitle>Working Calendar — review changes</DialogTitle>
            <DialogDescription>
              {importFile?.name} — {importDiff?.total_rows || 0} row{importDiff?.total_rows === 1 ? "" : "s"} parsed, {importDiff?.changes?.length || 0} change{importDiff?.changes?.length === 1 ? "" : "s"} detected.
            </DialogDescription>
          </DialogHeader>
          {importDiff?.warnings?.length ? (
            <div className="border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs" data-testid="tracker-import-warnings">
              <div className="font-semibold text-amber-800 dark:text-amber-300 mb-1">Warnings</div>
              <ul className="list-disc pl-5 space-y-0.5 text-amber-800 dark:text-amber-200">
                {(importDiff.warnings || []).map((w) => <li key={w}>{w}</li>)}
              </ul>
            </div>
          ) : null}
          <div className="max-h-96 overflow-auto border border-border" data-testid="tracker-import-diff">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-secondary">
                <tr className="text-left">
                  <th className="px-2 py-1.5 font-semibold">Item</th>
                  <th className="px-2 py-1.5 font-semibold">SME — current → new</th>
                  <th className="px-2 py-1.5 font-semibold">Last Done — current → new</th>
                  <th className="px-2 py-1.5 font-semibold">Frequency — current → new</th>
                </tr>
              </thead>
              <tbody>
                {(importDiff?.changes || []).length === 0 && (
                  <tr><td colSpan={4} className="px-2 py-6 text-center text-muted-foreground">No changes detected. The workbook matches the current state.</td></tr>
                )}
                {(importDiff?.changes || []).map((c) => (
                  <tr key={c.item_id} className="border-t border-border/60 align-top">
                    <td className="px-2 py-1.5 font-mono whitespace-nowrap">
                      {c.standard_id} {c.req_number}{c.subpart ? `.${c.subpart}` : ""}
                      <div className="text-[11px] text-muted-foreground font-sans max-w-xs truncate">{c.description}</div>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      {(c.current_sme || "") === (c.new_sme || "") ? (
                        <span className="text-muted-foreground">unchanged</span>
                      ) : (<>
                        <span className="text-muted-foreground line-through">{c.current_sme || "—"}</span>
                        <span className="mx-1">→</span>
                        <span className="text-primary font-semibold">{c.new_sme || "—"}</span>
                      </>)}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap font-mono">
                      {(c.current_last_completed || "") === (c.new_last_completed || "") ? (
                        <span className="text-muted-foreground">unchanged</span>
                      ) : (<>
                        <span className="text-muted-foreground line-through">{c.current_last_completed || "—"}</span>
                        <span className="mx-1">→</span>
                        <span className="text-primary font-semibold">{c.new_last_completed || "—"}</span>
                      </>)}
                    </td>
                    <td className="px-2 py-1.5 font-mono">
                      {JSON.stringify(c.current_frequency) === JSON.stringify(c.new_frequency) ? (
                        <span className="text-muted-foreground">unchanged</span>
                      ) : (<>
                        <span className="text-muted-foreground line-through">{JSON.stringify(c.current_frequency)}</span>
                        <span className="mx-1">→</span>
                        <span className="text-primary font-semibold">{JSON.stringify(c.new_frequency)}</span>
                      </>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setImportOpen(false)}>Cancel</Button>
            <Button data-testid="tracker-import-apply-btn"
              className="rounded-none bg-primary hover:bg-primary/90"
              onClick={applyImport}
              disabled={importBusy || !(importDiff?.changes?.length)}>
              {importBusy ? "Applying…" : `Apply ${importDiff?.changes?.length || 0} change${importDiff?.changes?.length === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
